<template>
  <div class="page">
    <h1 class="page-title">Шахматка - трудоемкость</h1>

    <div v-if="loading" class="loading">Загрузка...</div>
    <div v-else-if="error" class="error">Ошибка: {{ error }}</div>

    <div v-else class="dirs-grid-wrapper">
      <div v-for="(dir, dirKey) in normalizedDirs" :key="dirKey" class="dir-block">
        <div class="dir-header">
          <h3 class="dir-title">{{ dirKey }}</h3>
        </div>

        <div class="grid-wrapper">
          <div class="grid" :style="gridStyle(dirKey)" role="grid" :aria-label="`Таблица координат для ${dirKey}`">
            <template v-for="y in dir.maxY">
              <template v-for="x in dir.maxX">
                <div :key="x + '-' + y" class="cell" role="gridcell">
                  <!-- есть объект -->
                  <template v-if="cellAt(dirKey, x, y)">
                    <div class="obj-name" :title="cellAt(dirKey, x, y).name">
                      {{ cellAt(dirKey, x, y).name }}
                    </div>

                    <!-- Внутренний прямоугольник 112×58 — только он получает цвет -->
                    <div class="inner" :style="innerStyle(cellAt(dirKey, x, y))">
                      <div v-if="hasPercent(cellAt(dirKey, x, y))" class="pct">
                        {{ fmtPct(cellAt(dirKey, x, y).percentagePlan) }}
                      </div>
                    </div>

                    <div class="executor" :title="cellAt(dirKey, x, y).executor">
                      {{ shortExecutor(cellAt(dirKey, x, y).executor) }}
                    </div>
                  </template>

                  <!-- пустая координата -->
                  <template v-else>
                    <div class="obj-name"></div>
                    <div class="inner inner--empty"></div>
                    <div class="executor"></div>
                  </template>
                </div>
              </template>
            </template>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import firebase from "firebase";
import {apiUrl} from "../../../plugins/firebase";

export default {
  name: 'DirsGridPage',
  data() {
    return {
      payload: null,
      loading: true,
      error: '',
      // размеры внешней (прозрачной) ячейки грид-сетки
      cellWidth: 172,
      cellHeight: 104,
      // размеры внутреннего прямоугольника
      innerWidth: 162,
      innerHeight: 58,
      apiUrl:
        'https://proxy-functions.stroybots.ru/?url=https%3A%2F%2Fus-central1-fir-stroybots.cloudfunctions.net%2FonAggregateDirsByObjects%2FDQwoGwiTaNeOS18hPXcX&queryParams=uid=MK7v1PleUdPegW2FHFyT7GrziPS2',
    };
  },
  computed: {
    normalizedDirs() {
      const src =
        (this.payload &&
          this.payload.data &&
          this.payload.data.dirsByObjects &&
          this.payload.data.dirsByObjects.dirs) ||
        {};
      const result = {};
      Object.keys(src).forEach((dirKey) => {
        const objects = (src[dirKey] && src[dirKey].objects) || {};
        const map = new Map();
        let maxX = 0;
        let maxY = 0;
        Object.keys(objects).forEach((k) => {
          const o = objects[k];
          const x = Number(o.x) || 0;
          const y = Number(o.y) || 0;
          if (!x || !y) return;
          map.set(`${x},${y}`, o);
          if (x > maxX) maxX = x;
          if (y > maxY) maxY = y;
        });
        result[dirKey] = { coordMap: map, maxX: Math.max(maxX, 1), maxY: Math.max(maxY, 1) };
      });
      return result;
    },
  },
  mounted() {
    this.fetchData();
  },
  methods: {
    async fetchData() {
      this.loading = true;
      this.error = '';
      try {
        const uid = firebase.auth().currentUser.uid;
        const api_url = `${apiUrl}%2FonAggregateDirsByObjects%2F${this.$store.state.project.id}&queryParams=uid=${uid}`
        const res = await fetch(api_url);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        this.payload = await res.json();
      } catch (e) {
        console.error(e);
        this.error = e && e.message ? e.message : 'Не удалось загрузить данные';
      } finally {
        this.loading = false;
      }
    },
    gridStyle(dirKey) {
      const meta = this.normalizedDirs[dirKey];
      const cols = meta ? meta.maxX : 1;
      return {
        display: 'grid',
        gridTemplateColumns: `repeat(${cols}, ${this.cellWidth}px)`,
        gridAutoRows: `${this.cellHeight}px`,
      };
    },
    cellAt(dirKey, x, y) {
      const meta = this.normalizedDirs[dirKey];
      if (!meta) return null;
      return meta.coordMap.get(`${x},${y}`) || null;
    },
    hasPercent(cell) {
      const n = Number(cell && cell.percentagePlan);
      return Number.isFinite(n);
    },
    innerStyle(cell) {
      // красим только внутренний прямоугольник (112×58)
      const style = {
        width: this.innerWidth + 'px',
        height: this.innerHeight + 'px',
        borderRadius: '8px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
      };

      // пустая координата: пунктирная рамка, без заливки
      if (!cell) {
        style.border = '2px dashed #cbd5e1';
        style.background = 'transparent';
        return style;
      }

      // нет процента: оранжевая заливка, без текста (вывод текста контролируется hasPercent)
      if (cell.percentagePlan === "") {
        style.background = '#f2f3f5'; // orange @ 20%
        style.border = '2px dashed #cbd5e1';
        return style;
      }

      // есть процент: HSL-градус 0..120 (красный → жёлтый → зелёный)
      const raw = Number(cell.percentagePlan);
      const p = Math.max(0, Math.min(100, raw)); // clamp 0..100 для цвета
      const hue = (p / 100) * 120; // 0=red, 60=yellow, 120=green
      style.background = `hsl(${hue}, 85%, 90%)`;
      style.border = `2px solid hsl(${hue}, 70%, 45%)`;
      return style;
    },
    fmtPct(v) {
      if(v === '') return  ''
      const n = Number(v);
      if (!Number.isFinite(n)) return '';
      // показываем фактическое значение, даже если >100 (цвет — до 100)
      return (Math.round(n * 10) / 10).toString().replace('.', ',') + '%';
    },
    shortExecutor(name) {
      if (!name) return '';
      return name.replace(/\s+ООО\s+/gi, 'ООО ').replace(/\s+\"/g, ' "').trim();
    },
  },
};
</script>

<style scoped>
.page {
  padding: 16px;
  display: flex;
  max-width: 1200px;
  width: calc(100% - 12px);
  margin: 0 auto;
  flex-direction: column;
  gap: 16px;
}
.page-title {
  font-size: 20px;
  font-weight: 600;
  margin: 0;
}
.loading { font-size: 14px; color: #6b7280; }
.error   { font-size: 14px; color: #b91c1c; }

/* блоки по dirs */
.dirs-grid-wrapper { display: grid; grid-gap: 24px; }
.dir-block {
  max-width: 1200px;
  overflow-x: auto;
  border: 1px solid #e5e7eb;
  border-radius: 4px;
  padding: 12px;
  padding-bottom: 26px;
  background: #fff;
}
.dir-header { display: flex; align-items: center; justify-content: space-between; margin-bottom: 8px; gap: 12px; }
.dir-title  { margin: 0; font-size: 16px; font-weight: 600; }

/* скролл по горизонтали, если много колонок */
.grid-wrapper { overflow-x: auto; overflow-y: hidden; }
.grid { display: inline-grid; }

/* прозрачная внешняя ячейка сетки 128×74 */
.cell {
  width: 172px;
  height: 104px;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  /* фона нет — НЕ красим gridcell */
  background: transparent;
}

/* верхняя надпись — имя объекта */
.obj-name {
  position: absolute;
  top: 2px;
  left: 4px;
  right: 4px;
  font-size: 15px;
  font-weight: 700;
  line-height: 1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  color: #111827;
}

/* ВНУТРЕННИЙ прямоугольник 112×58 — он и цветной */
.inner {
  width: 162px;
  height: 58px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
}
.inner--empty { border: 2px dashed #cbd5e1; background: transparent; }

/* текст процента по центру (только если есть процент) */
.pct { font-size: 14px; font-weight: 700; color: #111827; }

/* нижняя надпись — исполнитель (название компании) */
.executor {
  position: absolute;
  bottom: 2px;
  left: 4px;
  right: 4px;
  font-size: 12px;
  line-height: 1;
  color: #374151;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
</style>



// WEBPACK FOOTER //
// src/views/Dashboard/Analytics/ChessVolume.vue